// ─── src/hooks/useMeetupAttendees.ts ──────────────────────────────────────────

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  checkInById,
  checkInByToken,
  deleteMeetupAttendee,
  getMeetupAttendees,
  undoCheckIn,
  updateMeetupAttendeeApproval,
} from "../api/meetupAttendees";
import { meetupEventKeys } from "./useMeetupEvents";
import type { MeetupAttendeeParams } from "../types/meetupAttendee";

export const meetupAttendeeKeys = {
  all:  ["meetupAttendees"] as const,
  list: (p: MeetupAttendeeParams) => [...meetupAttendeeKeys.all, "list", p] as const,
};

export const useMeetupAttendees = (params: MeetupAttendeeParams, enabled = true) =>
  useQuery({
    queryKey:        meetupAttendeeKeys.list(params),
    queryFn:         () => getMeetupAttendees(params),
    placeholderData: keepPreviousData,
    staleTime:       15_000,
    enabled,
  });

export const useUpdateMeetupAttendeeApproval = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: string; accepted: boolean }) =>
      updateMeetupAttendeeApproval(id, accepted),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all });
      // Rejecting frees a seat and may promote someone off the waitlist.
      qc.invalidateQueries({ queryKey: meetupEventKeys.all });
    },
  });
};

/** Scan-to-check-in. Returns the result code so the scanner can colour it. */
export const useCheckInByToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token, eventId }: { token: string; eventId?: string }) =>
      checkInByToken(token, eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all }),
  });
};

export const useCheckInById = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkInById(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all }),
  });
};

export const useUndoCheckIn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => undoCheckIn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all }),
  });
};

export const useDeleteMeetupAttendee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeetupAttendee(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetupAttendeeKeys.all });
      // Removing/promoting attendees changes each event's confirmed/waitlist counts.
      qc.invalidateQueries({ queryKey: meetupEventKeys.all });
    },
  });
};
